import React, { useEffect, useState } from 'react';
import type { Node } from 'react';
import styled from '@emotion/styled';
import { Field, Separator, Select, Option } from '@vmos2/design-system';
import { validation } from '@vmos2/utils';
import { useTranslation } from 'react-i18next';
import { split, first, isEmpty } from 'lodash';

type Props = {
  Form: Node,
  address: {
    address1: string;
    address2: string;
    town: string;
    country: string;
    postcode: string;
    label: string;
  },
  onChange: Function,
  setErrors: Function,
};

export const AddressDetails = ({ Form, address, onChange, setErrors }: Props) => {
  const { t } = useTranslation('delivery');
  const { validationType } = validation;
  const [selectedLabel, setSelectedLabel] = useState();

  const validationSchema = {
    address1: [validationType.isRequired],
    town: [validationType.isRequired],
    country: [validationType.isRequired],
    postcode: [validationType.isRequired],
  }

  const validateFields = (props) => {
    const errors = {};

    Object.keys(validationSchema).forEach((key) => {
      const field = props[key];
      const fieldSchema = validationSchema[key];
      if (fieldSchema) {
        fieldSchema.forEach((schema) => {
          switch (schema) {
            case validationType.isRequired:
              if (isEmpty(field)) {
                errors[key] = true;
              }
              break;
            default:
              break;
          }
        });
      }
    });

    return errors;
  }

  useEffect(() => {
    const errors = validateFields(address);
    setErrors(!isEmpty(errors));
  }, [address]);

  const getValidationSchema = (key) => validationSchema[key]?.map((schemaKey) => validation[schemaKey]()) || null;
  const options = [
    { name: t('deliveryOptionHome'), value: 'home' },
    { name: t('deliveryOptionWork'), value: 'work' },
    { name: t('deliveryOptionOther'), value: 'other' },
  ];

  const handleLabelSelect = ({ target: { value } }) => {
    setSelectedLabel(value);
    if (value !== 'other') {
      const option = options.find(option => option.value === value);
      onChange('label', option.name);
    }
  };

  return (
    <Form validate={validateFields}>
      <FormWrapper>
        <Field
          label={t('address1')}
          name="address1"
          validate={getValidationSchema('address1')}
          size="small"
          fullWidth
          defaultValue={first(split(address.address1, ',')) || ''}
          handleChange={value => onChange('address1', value)}
          mb={6}
        />

        <Field
          label={t('address2')}
          name="address2"
          validate={getValidationSchema('address2')}
          size="small"
          fullWidth
          defaultValue={address.address2}
          handleChange={value => onChange('address2', value)}
          mb={6}
        />

        <FieldWrapper>
          <Field
            label={t('town')}
            name="town"
            validate={getValidationSchema('town')}
            size="small"
            fullWidth
            defaultValue={address.town}
            handleChange={value => onChange('town', value)}
            mb={6}
          />
          <Field
            label={t('county')}
            name="country"
            validate={getValidationSchema('country')}
            size="small"
            fullWidth
            defaultValue={address.country}
            handleChange={value => onChange('country', value)}
            mb={6}
          />
        </FieldWrapper>

        <Field
          label={t('postCode')}
          name="postcode"
          parse={value => (value ? value.toUpperCase() : '')}
          validate={getValidationSchema('postcode')}
          size="small"
          fullWidth
          defaultValue={address.postcode}
          handleChange={value => onChange('postcode', value)}
          mb={6}
        />
      </FormWrapper>

      <Separator />

      <FormWrapper>
        {selectedLabel === 'other' || address?.label ? (
          <Field
            label={t('addressLabel')}
            name="label"
            validate={getValidationSchema('label')}
            size="small"
            fullWidth
            defaultValue={address.label}
            handleChange={value => onChange('label', value)}
            mb={6}
          />
        ) : (
          <>
            <label htmlFor="userProfileLabel">{t('addressLabel')}</label>
            <Select id="userProfileLabel" onChange={handleLabelSelect} mt={2} name="user-address-label">
              <Option value="">{t('deliveryOptionSelectLabel')}</Option>
              {
                options.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.name}
                  </Option>
                ))
              }
            </Select>
          </>
        )}
      </FormWrapper>
    </Form>
  );
};

 const FieldWrapper = styled('div')`
   display: grid;
   grid-auto-flow: column;
   gap: 16px;
 `;

const FormWrapper = styled('div')`
  margin-top: 16px;

  @media (${({ theme }) => theme.breakpoint.tabletLarge}) {
    width: 360px;
  }
`;
